import React from "react";
import {
  Layers,
  ShieldCheck,
  Eye,
  Building2,
  Coins,
  Banknote,
  CircuitBoard,
  Sparkles,
  HeartPulse,
  Home as HomeIcon,
} from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";

const SOLUTIONS = [
  { icon: Banknote, title: "Revolving Credit Facilities", body: "Flexible financing solutions for working capital." },
  { icon: Layers, title: "Unitranche / Senior Stretch", body: "Simplified capital structure with competitive pricing." },
  { icon: Layers, title: "Second-Lien Loans", body: "Subordinated debt for growth initiatives." },
  { icon: Coins, title: "Junior Capital / Mezzanine", body: "Hybrid financing solutions." },
  { icon: CircuitBoard, title: "Crypto Finance Solutions", body: "Innovative digital asset financing." },
];

const WHY = [
  { title: "First Access", body: "See opportunities before the broader market." },
  { title: "Enhanced Diligence", body: "Deep involvement in every transaction." },
  { title: "Greater Control", body: "Active role in structuring deals." },
  { title: "PE Partnerships", body: "First-call partner to leading firms." },
];

const PROCESS = [
  { step: "01", title: "Initial Screening", body: "Senior credit officers review opportunities." },
  { step: "02", title: "Deep Analysis", body: "Team of professionals conducts thorough research." },
  { step: "03", title: "Committee Review", body: "Investment Committee evaluates risk-adjusted returns." },
  { step: "04", title: "Execution", body: "Timely execution with full transparency." },
];

const GENERAL_INDUSTRIES = [
  "Aerospace & Defence",
  "Aftermarket Automotive",
  "Business Services",
  "Consumer Products",
  "Distribution",
  "Food & Beverage",
  "Oil & Gas",
  "Renewable Energy",
  "General Manufacturing",
  "Industrials & Infrastructure",
  "Packaging",
  "Speciality Chemicals",
  "Speciality Retail",
  "Technology, Media & Telecom",
  "Cryptocurrency, AI & Web3",
  "Healthcare & Greentech",
  "Real Estate",
];

const HEALTHCARE = [
  "Healthcare Services",
  "Healthcare Information Technology",
  "Medical Device Products & Manufacturing",
  "Dental Services",
  "Facility-Based Services",
  "Mayor Services",
  "Physician-Practice Management",
  "Post-Acute Services",
  "Outpatient Services",
];

const PE_PARTNERS = ["Warburg Pincus", "Kohlberg & Company", "Water Street", "Webster Capital", "Wellspring Capital"];

export default function BorrowersInvestorsPage() {
  return (
    <div data-testid="borrowers-page" className="page-fade">
      <PageHero
        eyebrow="Lending Solutions"
        title={<>Borrowers <span className="italic">&</span> Investors.</>}
        lede="Direct access to loans sourced by a leading middle market lender."
        image="https://images.pexels.com/photos/14936005/pexels-photo-14936005.jpeg"
        testId="borrowers-hero"
      />

      {/* About */}
      <section className="bg-[#070B14] py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">About EMIVIA</div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              A partner you can <span className="italic">trust.</span>
            </h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 space-y-6 text-white/70 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            <p>
              We are a private investment company specialised in wealth and asset management
              advisory. We manage client funds individually and provide personalised support to
              each investor.
            </p>
            <p>
              With the advice we provide to investors, we offer an interest guarantee on
              investments in real estate and finance, ensuring the security and growth of your
              wealth.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/5 mt-8">
              {[
                { k: "Real Estate", v: "Guaranteed investments" },
                { k: "Finance", v: "Personalised asset management" },
                { k: "Guarantee", v: "Security on your investments" },
              ].map((c) => (
                <div key={c.k} className="bg-[#070B14] p-6">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">{c.k}</div>
                  <div className="mt-3 text-white/80 text-sm font-light">{c.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Senior direct lending */}
      <section className="relative py-24 sm:py-32 bg-[#0B1120] border-y border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              Versatile Financing Solutions
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Senior Direct <span className="italic">Lending</span> Program.
            </h2>
            <p className="mt-8 text-white/70 text-base sm:text-lg font-light leading-relaxed">
              EMIVIA LIMITED serves leading middle market companies and private equity firms.
              We build lasting relationships as a reliable financing partner that supports growing businesses.
            </p>
            <p className="mt-4 text-white/70 text-base sm:text-lg font-light leading-relaxed">
              We offer competitive products across the debt capital structure, primarily serving
              borrowers with{" "}
              <span className="text-[#D4AF37] font-medium">£5 million to £40 million</span> of EBITDA.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {[
              { icon: Sparkles, title: "Flexible Capital", body: "Minimum £500K hold size enables total financing solutions." },
              { icon: ShieldCheck, title: "Experienced Team", body: "Deep experience in structuring, underwriting and managing investments." },
              { icon: Eye, title: "Reliable Partner", body: "Supporting firms across the complete investment lifecycle." },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 80} className="bg-[#070B14] p-10">
                  <Icon size={26} strokeWidth={1.25} className="text-[#D4AF37]" />
                  <h3 className="mt-6 font-serif-display text-white text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-4 text-white/65 text-sm font-light leading-relaxed">{p.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Financing solutions list */}
      <section className="bg-[#070B14] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">Our Financing Solutions</div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight max-w-3xl">
              Across the full debt capital structure.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5">
            {SOLUTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 70} className="bg-[#070B14] p-8 hover:bg-[#0F1523] transition-colors duration-500">
                  <Icon size={24} strokeWidth={1.25} className="text-[#D4AF37]" />
                  <h3 className="mt-6 font-serif-display text-white text-xl tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-white/60 text-sm font-light leading-relaxed">{s.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why invest with us */}
      <section className="bg-[#0B1120] border-y border-white/5 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">Why Invest With Us</div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              The advantage of a <span className="italic">lead lender.</span>
            </h2>
            <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed font-light">
              We believe the best way to invest in middle market credit is as a lead lender.
              Lead lenders see opportunities first and benefit from enhanced diligence, greater
              control, and an active role in structuring transactions.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 80} className="bg-[#070B14] p-10">
                <div className="font-serif-display text-[#D4AF37] text-5xl">0{i + 1}</div>
                <h3 className="mt-6 font-serif-display text-white text-xl tracking-tight">{w.title}</h3>
                <p className="mt-3 text-white/65 text-sm font-light leading-relaxed">{w.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process timeline */}
      <section className="bg-[#070B14] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">How We Work</div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Our Investment Process.
            </h2>
          </Reveal>

          <div className="mt-16 relative">
            <div className="hidden lg:block absolute left-0 right-0 top-[58px] h-px bg-white/10" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 relative">
              {PROCESS.map((p, i) => (
                <Reveal key={p.step} delay={i * 100} className="relative">
                  <div className="w-12 h-12 rounded-full border border-[#D4AF37] bg-[#070B14] flex items-center justify-center text-[#D4AF37] font-serif-display text-lg">
                    {p.step}
                  </div>
                  <h3 className="mt-8 font-serif-display text-white text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-white/65 text-sm font-light leading-relaxed">{p.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-[#0B1120] border-y border-white/5 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Building2 size={28} strokeWidth={1.25} className="text-[#D4AF37]" />
            <h3 className="mt-6 font-serif-display text-white text-3xl sm:text-4xl tracking-tight leading-tight">
              General Industries
            </h3>
            <p className="mt-4 text-white/65 font-light leading-relaxed max-w-md">
              Our General Industries team has expertise structuring, understanding and managing
              investments across the broader economy.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {GENERAL_INDUSTRIES.map((ind) => (
                <li
                  key={ind}
                  className="text-[11px] uppercase tracking-[0.18em] text-white/70 border border-white/10 px-3 py-2 hover:border-[#D4AF37]/50 hover:text-white transition-colors"
                >
                  {ind}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={120}>
            <HeartPulse size={28} strokeWidth={1.25} className="text-[#D4AF37]" />
            <h3 className="mt-6 font-serif-display text-white text-3xl sm:text-4xl tracking-tight leading-tight">
              Healthcare
            </h3>
            <p className="mt-4 text-white/65 font-light leading-relaxed max-w-md">
              Our Healthcare team brings industry relationships, sector perspective and
              transaction knowledge to each opportunity we evaluate — including elements of
              reimbursement risk.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {HEALTHCARE.map((ind) => (
                <li
                  key={ind}
                  className="text-[11px] uppercase tracking-[0.18em] text-white/70 border border-white/10 px-3 py-2 hover:border-[#D4AF37]/50 hover:text-white transition-colors"
                >
                  {ind}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Underwriting / Transparency */}
      <section className="bg-[#070B14] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">Disciplined Underwriting</div>
            <h3 className="mt-6 font-serif-display text-white text-3xl sm:text-4xl tracking-tight leading-tight">
              Protecting & growing capital.
            </h3>
            <p className="mt-6 text-white/70 leading-relaxed font-light">
              We are committed to protecting and growing our investors&apos; capital. Our team has
              decades of investment experience and applies a rigorous buy-side underwriting
              approach to every opportunity we consider.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">Deep Transparency</div>
            <h3 className="mt-6 font-serif-display text-white text-3xl sm:text-4xl tracking-tight leading-tight">
              Flexible, not one-size-fits-all.
            </h3>
            <p className="mt-6 text-white/70 leading-relaxed font-light">
              We offer flexible investment solutions with deep transparency, not one-size-fits-all
              products. Each investment undergoes a multistep underwriting process that begins
              with a thematic view of industry fundamentals.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Startup support */}
      <section className="bg-[#0B1120] border-y border-white/5 py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.pexels.com/photos/8082233/pexels-photo-8082233.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120] via-[#0B1120]/85 to-[#0B1120]/50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 max-w-4xl">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              Supporting Innovation & Growth
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Post-COVID Era: Our Commitment to <span className="italic">Startups.</span>
            </h2>
          </Reveal>
          <Reveal delay={120} className="mt-8 space-y-6 text-white/70 leading-relaxed font-light max-w-3xl">
            <p>
              Following the transformative period of COVID-19, we have developed a particular
              passion for supporting startups and emerging companies in their development and
              fundraising phases. Innovation and entrepreneurship are the engines of tomorrow&apos;s
              economy.
            </p>
            <p>
              Our approach is built on discretion and confidentiality. Whether you&apos;re seeking
              seed funding, Series A/B rounds, or bridge financing, we work behind the scenes to
              structure solutions that respect your vision while protecting sensitive information.
            </p>
            <p>
              We maintain strict confidentiality throughout the entire process — from initial
              discussions to final execution.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PE Partnerships */}
      <section className="bg-[#070B14] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              Selected Private Equity Relationships
            </div>
            <h3 className="mt-6 font-serif-display text-white text-3xl sm:text-4xl tracking-tight leading-tight">
              Trusted partnerships with leading global firms.
            </h3>
          </Reveal>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/5 border border-white/5">
            {PE_PARTNERS.map((p, i) => (
              <Reveal
                key={p}
                delay={i * 60}
                className="bg-[#070B14] aspect-[4/3] flex flex-col items-center justify-center"
              >
                <div className="w-14 h-14 rounded-full border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] font-serif-display text-2xl">
                  {p.charAt(0)}
                </div>
                <div className="mt-4 text-white/80 text-sm text-center px-3 font-light">{p}</div>
              </Reveal>
            ))}
          </div>

          <p className="mt-10 text-center text-[11px] uppercase tracking-[0.28em] text-white/40">
            Confidential relationships maintained with leading global institutions
          </p>
        </div>
      </section>

      <CtaSection
        testId="borrowers-cta"
        title="Partner with EMIVIA on your next transaction."
        description="Discuss financing strategies, structured deals or co-investment opportunities with our senior team."
      />
    </div>
  );
}
